import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { Highlight as HighlightData } from '../../types';
import { useReveal } from '../../hooks/useReveal';
import { useDeckFlip } from '../../hooks/useDeckFlip';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import Highlight from './Highlight';
import HighlightSpread from './HighlightSpread';
import HighlightBookmarks from './HighlightBookmarks';
import '../../styles/highlight/Highlight.css';
import '../../styles/highlight/HighlightDeck.css';
import '../../styles/highlight/HighlightBookmark.css';

interface HighlightDeckProps {
  highlights: HighlightData[];
  /** Autoplay interval in ms. Default 9000. */
  autoplayMs?: number;
}

const DEFAULT_AUTOPLAY_MS = 9000;

function plainTitle(t: HighlightData['title']): string {
  return [t.pre, t.accent, t.post].filter(Boolean).join('');
}

/**
 * Multi-highlight deck — wraps the single-highlight `<HighlightSpread />`
 * with paper bookmarks on the right margin and a 3-phase right-page flip
 * between variants. Owns:
 *   • the flip state machine (useDeckFlip)
 *   • autoplay timer (9s default, pauses on hover / hidden tab / off-screen)
 *   • the screen-reader live region announcing variant changes
 *
 * All variants are kept in the DOM; `.is-active` toggles which one renders.
 * Mounting/unmounting mid-flight would lose the animation, so we keep them
 * stacked even though only one is visible at a time.
 */
const HighlightDeck: React.FC<HighlightDeckProps> = ({
  highlights,
  autoplayMs = DEFAULT_AUTOPLAY_MS,
}) => {
  // Single-highlight fallback — delegate to the legacy <Highlight />, no bookmark column.
  if (highlights.length <= 1) {
    return <Highlight data={highlights[0] ?? null} />;
  }

  return <HighlightDeckInner highlights={highlights} autoplayMs={autoplayMs} />;
};

const HighlightDeckInner: React.FC<Required<HighlightDeckProps>> = ({
  highlights,
  autoplayMs,
}) => {
  const reduced = usePrefersReducedMotion();
  const { index, phase, goTo, next } = useDeckFlip({
    count: highlights.length,
    initial: 0,
    prefersReducedMotion: reduced,
  });

  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [docHidden, setDocHidden] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  // Bumped on every user-initiated selection so the autoplay interval is
  // torn down + recreated. Matches the mockup's `resetTimer()` behavior:
  // a clicked variant gets a fresh 9s window before auto-advance.
  const [selectionEpoch, setSelectionEpoch] = useState(0);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const { ref: revealRef, revealed } = useReveal<HTMLElement>();

  const baseId = useId().replace(/[:]/g, '');
  const panelId = `${baseId}-panel`;
  const bookmarkIdPrefix = `${baseId}-bm`;
  const liveId = `${baseId}-live`;

  const active = highlights[index];
  const title = plainTitle(active.title);

  // ── autoplay ────────────────────────────────────────────────────────────
  // Suspended whenever the user is interacting, the tab is hidden, the deck
  // is off-screen, or motion is reduced. The `auto` button is the only hard
  // pause; the rest are transient and resume automatically.
  const autoplaySuspended = paused || hovered || docHidden || !onScreen || reduced;

  useEffect(() => {
    if (autoplaySuspended) return undefined;
    const id = setInterval(() => next(), autoplayMs);
    return () => clearInterval(id);
    // selectionEpoch is intentionally in the dep array — bumping it
    // re-creates the interval, giving the new variant a fresh 9s window.
  }, [autoplaySuspended, autoplayMs, next, selectionEpoch]);

  // Pause on hover (whole deck stage).
  const onMouseEnter = useCallback(() => setHovered(true), []);
  const onMouseLeave = useCallback(() => setHovered(false), []);
  const onFocusCapture = useCallback(() => setHovered(true), []);
  const onBlurCapture = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
    // Only release once focus truly leaves the deck.
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setHovered(false);
    }
  }, []);

  // Pause when the tab is hidden.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVis = () => setDocHidden(document.hidden);
    setDocHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Pause when the deck scrolls off-screen.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setOnScreen(entry.isIntersecting),
      { threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const togglePaused = useCallback(() => setPaused((p) => !p), []);

  // User selected a tab → reset the autoplay window but don't hard-pause.
  // (The mockup matches: a clicked variant gets a fresh 9s before advance.)
  const onSelect = useCallback(
    (i: number) => {
      goTo(i);
      setSelectionEpoch((n) => n + 1);
    },
    [goTo],
  );

  // ── stage class ─────────────────────────────────────────────────────────
  const stageClass = [
    'psl-hl-deck',
    phase === 'flipping' && 'is-flipping',
    phase === 'resetting' && 'is-resetting',
    phase === 'flipping-back' && 'is-flipping-back',
  ]
    .filter(Boolean)
    .join(' ');

  const ariaLabel = `${active.chapterMark} — ${title}`;

  return (
    <section
      ref={revealRef}
      aria-label={ariaLabel}
      aria-roledescription="accordion"
      className={`psl-hl-desk psl-reveal${revealed ? ' is-in-view' : ''}`}
    >
      <div
        ref={stageRef}
        className={stageClass}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
      >
        <div
          className="psl-hl-deck__panel"
          id={panelId}
          role="tabpanel"
          aria-labelledby={`${bookmarkIdPrefix}-${index}`}
        >
          {highlights.map((h, i) => (
            <HighlightSpread
              key={h.id}
              data={h}
              ariaTitle={plainTitle(h.title)}
              className={`psl-hl-variant${i === index ? ' is-active' : ''}`}
            />
          ))}
        </div>

        <HighlightBookmarks
          highlights={highlights}
          activeIndex={index}
          paused={paused}
          panelId={panelId}
          bookmarkIdPrefix={bookmarkIdPrefix}
          onSelect={onSelect}
          onTogglePaused={togglePaused}
        />

        <div
          id={liveId}
          className="psl-sr-only"
          aria-live="polite"
          aria-atomic="true"
        >
          {`Destaque ${index + 1} de ${highlights.length}: ${active.bookmarkKind}`}
        </div>
      </div>
    </section>
  );
};

export default HighlightDeck;
